import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import logger from '../../utils/logger';

/**
 * 組織作成データの型定義
 */
export interface OrganizationCreationData {
  organizationName: string;
  businessType: SalonBusinessType;
  address?: string;
  phoneNumber?: string;
  ownerName: string;
  email: string;
  description?: string;
}

/**
 * サロン業種の型定義
 */
export type SalonBusinessType =
  | 'beauty_salon'           // 美容室
  | 'relaxation_salon'       // リラクゼーション
  | 'nail_salon'             // ネイルサロン
  | 'eyelash_salon'          // アイラッシュ
  | 'esthetic_salon'         // エステサロン
  | 'head_spa'               // ヘッドスパ
  | 'barbershop'             // 理容室
  | 'other';                 // その他

/**
 * 組織作成結果の型定義
 */
export interface OrganizationCreationResult {
  success: boolean;
  organizationId?: string;
  message: string;
  // セットアップウィザード用の初期データ
  initialSetupData?: {
    organizationName: string;
    businessType: string;
    description: string;
    address: {
      postalCode: string;
      prefecture: string;
      city: string;
      address1: string;
      address2: string;
    };
    phoneNumbers: {
      main: string;
      reservation: string;
    };
  };
}

/**
 * プロパティの型定義
 */
interface OrganizationCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: OrganizationCreationResult) => void;
  userInfo?: {
    name: string;
    email: string;
  };
}

/**
 * 業種選択肢の定義
 */
const BUSINESS_TYPES = [
  { value: 'beauty_salon', label: '美容室', icon: '💇‍♀️', description: 'カット・カラー・パーマなど' },
  { value: 'head_spa', label: 'ヘッドスパ', icon: '🧠', description: 'ヘッドマッサージ・頭皮ケア' },
  { value: 'relaxation_salon', label: 'リラクゼーション', icon: '🧘‍♀️', description: 'マッサージ・アロマなど' },
  { value: 'nail_salon', label: 'ネイルサロン', icon: '💅', description: 'ネイルケア・ネイルアート' },
  { value: 'eyelash_salon', label: 'アイラッシュ', icon: '👁️', description: 'まつげエクステ・パーマ' },
  { value: 'esthetic_salon', label: 'エステサロン', icon: '✨', description: 'フェイシャル・ボディケア' },
  { value: 'barbershop', label: '理容室', icon: '✂️', description: '理容・シェービング' },
  { value: 'other', label: 'その他', icon: '🏪', description: 'その他の美容サービス' }
] as const;

/**
 * Phase 4: 組織作成モーダルコンポーネント
 * メール認証完了後に表示される組織作成フォーム
 */
const OrganizationCreationModal: React.FC<OrganizationCreationModalProps> = ({
  open,
  onClose,
  onSuccess,
  userInfo
}) => {
  // フォーム状態
  const [formData, setFormData] = useState<OrganizationCreationData>({
    organizationName: '',
    businessType: 'head_spa', // デフォルトはヘッドスパ（HITOYASUMIに合わせて）
    address: '',
    phoneNumber: '',
    ownerName: userInfo?.name || '',
    email: userInfo?.email || '',
    description: ''
  });

  // UI状態
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1); // 1: 基本情報, 2: 詳細情報, 3: 確認

  /**
   * フォームデータ更新
   */
  const updateFormData = useCallback((updates: Partial<OrganizationCreationData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // エラーをクリア
    if (error) {
      setError(null);
    }
  }, [error]);

  /**
   * バリデーション
   */
  const validateForm = useCallback((): string | null => {
    if (!formData.organizationName.trim()) {
      return '組織名を入力してください';
    }
    if (formData.organizationName.length < 2) {
      return '組織名は2文字以上で入力してください';
    }
    if (!formData.ownerName.trim()) {
      return 'オーナー名を入力してください';
    }
    if (!formData.email.trim()) {
      return 'メールアドレスを入力してください';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return '有効なメールアドレスを入力してください';
    }
    return null;
  }, [formData]);

  /**
   * 次のステップへ進む
   */
  const handleNextStep = useCallback(() => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setStep(prev => Math.min(prev + 1, 3));
  }, [validateForm]);

  /**
   * 前のステップに戻る
   */
  const handlePrevStep = useCallback(() => {
    setStep(prev => Math.max(prev - 1, 1));
    setError(null);
  }, []);

  /**
   * 組織作成処理
   */
  const handleCreateOrganization = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      logger.log('🏢 [OrganizationCreation] 組織作成開始:', formData.organizationName);

      // TODO: 実際のAPI呼び出しを実装
      // 現在はモック実装
      await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5秒の遅延でAPI呼び出しをシミュレート

      const result: OrganizationCreationResult = {
        success: true,
        organizationId: `org-${Date.now()}`,
        message: '組織が正常に作成されました',
        // セットアップウィザード用の初期データを含める
        initialSetupData: {
          organizationName: formData.organizationName || 'New Organization',
          businessType: formData.businessType || 'general',
          description: formData.description || '',
          address: {
            postalCode: '',
            prefecture: '',
            city: '',
            address1: formData.address ?? '',
            address2: ''
          },
          phoneNumbers: {
            main: formData.phoneNumber ?? '',
            reservation: ''
          }
        }
      };

      logger.log('✅ [OrganizationCreation] 組織作成成功:', result.organizationId);

      // 成功コールバック実行
      onSuccess(result);
    } catch (error) {
      logger.error('❌ [OrganizationCreation] 組織作成エラー:', error);
      setError('組織の作成に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onSuccess]);

  /**
   * モーダルクローズ処理
   */
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  /**
   * 選択された業種情報を取得
   */
  const selectedBusinessType = BUSINESS_TYPES.find(type => type.value === formData.businessType);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <BusinessIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                組織を作成しましょう
              </Typography>
              <Typography variant="body2" color="text.secondary">
                あなたの美容サロンの基本情報を入力してください
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 1: 基本情報 */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Stack spacing={3}>
                <Typography variant="h6" gutterBottom>
                  基本情報
                </Typography>

                <TextField
                  label="組織名・サロン名"
                  placeholder="例: HITOYASUMI ドライヘッドスパ"
                  fullWidth
                  required
                  value={formData.organizationName}
                  onChange={(e) => updateFormData({ organizationName: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <FormControl fullWidth required>
                  <InputLabel>業種</InputLabel>
                  <Select
                    value={formData.businessType}
                    label="業種"
                    onChange={(e) => updateFormData({ businessType: e.target.value as SalonBusinessType })}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {BUSINESS_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                            {type.icon}
                          </Typography>
                          <Box>
                            <Typography variant="body1">
                              {type.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedBusinessType && (
                  <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography component="span" sx={{ fontSize: '1.5rem' }}>
                        {selectedBusinessType.icon}
                      </Typography>
                      <Typography variant="body2" color="primary.main">
                        {selectedBusinessType.label}を選択しました
                      </Typography>
                    </Stack>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="オーナー名"
                      fullWidth
                      required
                      value={formData.ownerName}
                      onChange={(e) => updateFormData({ ownerName: e.target.value })}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="メールアドレス"
                      type="email"
                      fullWidth
                      required
                      value={formData.email}
                      onChange={(e) => updateFormData({ email: e.target.value })}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </motion.div>
          )}

          {/* Step 2: 詳細情報 */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Stack spacing={3}>
                <Typography variant="h6" gutterBottom>
                  詳細情報（任意）
                </Typography>

                <TextField
                  label="住所"
                  placeholder="例: 東京都渋谷区..."
                  fullWidth
                  value={formData.address}
                  onChange={(e) => updateFormData({ address: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  label="電話番号"
                  placeholder="例: 03-1234-5678"
                  fullWidth
                  value={formData.phoneNumber}
                  onChange={(e) => updateFormData({ phoneNumber: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  label="組織の説明"
                  placeholder="例: お客様に最高のリラクゼーション体験を提供します"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => updateFormData({ description: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Stack>
            </motion.div>
          )}

          {/* Step 3: 確認 */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Stack spacing={3}>
                <Typography variant="h6" gutterBottom>
                  内容確認
                </Typography>

                <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        組織名
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formData.organizationName}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        業種
                      </Typography>
                      <Chip
                        icon={<span style={{ fontSize: '1.2rem' }}>{selectedBusinessType?.icon}</span>}
                        label={selectedBusinessType?.label}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        オーナー
                      </Typography>
                      <Typography variant="body1">
                        {formData.ownerName} ({formData.email})
                      </Typography>
                    </Box>

                    {formData.address && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          住所
                        </Typography>
                        <Typography variant="body1">
                          {formData.address}
                        </Typography>
                      </Box>
                    )}

                    {formData.phoneNumber && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          電話番号
                        </Typography>
                        <Typography variant="body1">
                          {formData.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Alert severity="info">
                  <Typography variant="body2">
                    組織を作成すると、あなたが自動的にオーナーとして設定されます。
                    後からメンバーを招待することができます。
                  </Typography>
                </Alert>
              </Stack>
            </motion.div>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
          {/* 戻るボタン */}
          {step > 1 && (
            <Button
              variant="outlined"
              onClick={handlePrevStep}
              disabled={isSubmitting}
              sx={{ borderRadius: 2 }}
            >
              戻る
            </Button>
          )}

          {step < 1 && <Box />}

          {/* 次へ・作成ボタン */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {step < 3 ? (
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={isSubmitting}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                }}
              >
                次へ
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateOrganization}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                {isSubmitting ? '作成中...' : '組織を作成して開始'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default OrganizationCreationModal;